import type { ReactNode } from 'react'
import { Icon } from './icons'

/*
  The bar above a list of records and its sortable headings (owner, 2026-10-27): a search box,
  a dropdown for each column worth picking from, and how many rows are shown with a way back
  to all of them. ArrangedTable builds it for every TableWrap; the cattle list builds its own
  from the same parts because it searches EIDs and names the table does not show.
*/

export function TableToolbar({
  search,
  onSearch,
  placeholder = 'Search',
  searchId,
  shown,
  total,
  filtered,
  onClear,
  children,
  testId,
}: {
  search: string
  onSearch: (text: string) => void
  placeholder?: string
  searchId?: string
  shown: number
  total: number
  filtered: boolean
  onClear: () => void
  /** The dropdowns and toggles, after the search box. */
  children?: ReactNode
  testId?: string
}) {
  return (
    <div className="tableToolbar" role="search" data-testid={testId}>
      <label className="tableSearch">
        <Icon name="search" size={1} />
        <input
          id={searchId}
          type="search"
          aria-label={placeholder}
          placeholder={`${placeholder}…`}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </label>
      {children}
      {filtered && (
        <span className="tableCount" aria-live="polite">
          {shown} of {total}
          <button type="button" className="tableClear" onClick={onClear}>
            <Icon name="close" size={0.9} />
            Clear filters
          </button>
        </span>
      )}
    </div>
  )
}

/**
 * A column's values as a compact dropdown: its name, then the value picked or All. The whole
 * control is outlined in the accent while a value is picked, so a narrowed list says so.
 */
export function ToolbarSelect({
  label,
  value,
  values,
  onChange,
  id,
  format = (v) => v,
}: {
  label: string
  value: string
  values: readonly string[]
  onChange: (value: string) => void
  id?: string
  format?: (value: string) => string
}) {
  return (
    <label className="toolbarSelect" data-active={value ? true : undefined}>
      <span>{label}</span>
      <select id={id} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {values.map((v) => (
          <option key={v} value={v}>
            {format(v)}
          </option>
        ))}
      </select>
    </label>
  )
}

/** An on/off narrowing in the toolbar ("Unseen only"), shown as a pill that says it is on. */
export function ToolbarToggle({
  label,
  on,
  onChange,
}: {
  label: string
  on: boolean
  onChange: (on: boolean) => void
}) {
  return (
    <button type="button" className="pill" aria-pressed={on} onClick={() => onChange(!on)}>
      {label}
    </button>
  )
}

/**
 * The foot of a long list: which rows are showing of how many, and the pages either side.
 * Nothing when the list fits on one page.
 */
export function TablePager({
  page,
  pages,
  from,
  to,
  total,
  onPage,
  testId,
}: {
  /** Counted from 0. */
  page: number
  pages: number
  /** The first row shown, counted from 0, and the row after the last. */
  from: number
  to: number
  total: number
  onPage: (page: number) => void
  testId?: string
}) {
  if (pages <= 1) return null
  return (
    <nav className="tablePager" aria-label="Pages" data-testid={testId}>
      <span className="tableCount" aria-live="polite">
        {from + 1}–{Math.min(to, total)} of {total}
      </span>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        disabled={page === 0}
        onClick={() => onPage(page - 1)}
      >
        Previous
      </button>
      <span className="text-sm text-muted whitespace-nowrap">
        Page {page + 1} of {pages}
      </span>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        disabled={page >= pages - 1}
        onClick={() => onPage(page + 1)}
      >
        Next
      </button>
    </nav>
  )
}

export type SortState = 'ascending' | 'descending' | 'none'

/**
 * A heading that sorts its column: the name with a faint two-way arrow, which turns solid and
 * points the way the column runs once it is the sort. The arrow is drawn, not a ▲ in the text,
 * so the heading's accessible name is the column's name alone.
 */
export function SortButton({
  sort,
  onClick,
  children,
}: {
  sort: SortState
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className="sortButton"
      data-sorted={sort === 'none' ? undefined : true}
      onClick={onClick}
    >
      {children}
      <Icon
        name={sort === 'ascending' ? 'sortUp' : sort === 'descending' ? 'sortDown' : 'sort'}
        size={0.9}
      />
    </button>
  )
}
